import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as withdrawalsService from '../services/withdrawals.service';
import type {
  WithdrawalDTO,
  WithdrawalDetailDTO,
  PayoutReconcileReportDTO,
  PayoutReconcileRunResultDTO,
  WithdrawalSortOrder,
} from '../types/withdrawals.types';
import { useToastStore } from '../../../store/toast.store';

function parseSortOrder(value: string | null, fallback: WithdrawalSortOrder): WithdrawalSortOrder {
  return value === 'asc' || value === 'desc' ? value : fallback;
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'admin role required': '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      withdrawal_not_found: '提现记录不存在',
      'withdrawal request not found': '提现记录不存在',
      invalid_withdrawal_status: '当前状态不允许此操作',
      'invalid withdrawal status': '当前状态不允许此操作',
      invalid_withdrawal_action: '无效的审核操作',
      'invalid withdrawal action': '无效的审核操作',
      invalid_withdrawal_payment_proof: '打款截图格式不正确，请上传 PNG/JPG/WebP/GIF 图片',
      'invalid withdrawal payment proof': '打款截图格式不正确，请上传 PNG/JPG/WebP/GIF 图片',
      withdrawal_payment_proof_too_large: '打款截图过大，请控制在 2MB 以内',
      'withdrawal payment proof too large': '打款截图过大，请控制在 2MB 以内',
      'withdrawal payout provider is required': '提现记录缺少代付服务商，无法自动查单或重试',
      'withdrawal payout provider is disabled': '自动打款功能当前未启用',
      'withdrawal payout provider config is incomplete': '自动打款接口凭据未配置完整',
      'withdrawal payout channel is unavailable': '当前提现渠道未开启自动打款或缺少产品 ID',
      'withdrawal payout amount exceeds auto limit': '提现金额超过自动打款限额',
      'withdrawal payout amount mismatch': '三方返回金额与提现金额不一致，请人工核对',
      'invalid withdrawal payout status': '三方返回了无法识别的打款状态',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWithdrawalList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const marketId = searchParams.get('marketId') === 'VN' ? 'VN' : 'CN';
  const requestSequence = useRef(0);
  const status = searchParams.get('status') || '';
  const payoutProvider = searchParams.get('payoutProvider') || '';
  const userId = searchParams.get('userId') || '';
  const userKeyword = searchParams.get('userKeyword') || '';
  const startTime = searchParams.get('startTime') || '';
  const endTime = searchParams.get('endTime') || '';
  const order = parseSortOrder(searchParams.get('order'), 'desc');
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;

  const [items, setItems] = useState<WithdrawalDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);
  const [pendingTotalAmountFen, setPendingTotalAmountFen] = useState(0);
  const [pendingTotalAmountYuan, setPendingTotalAmountYuan] = useState('0.00');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (
    p: number,
    uid: string,
    keyword: string,
    s: string,
    provider: string,
    start: string,
    end: string,
    sortOrder: WithdrawalSortOrder,
  ) => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await withdrawalsService.listWithdrawals({
        marketId, page: p, pageSize, userId: uid, userKeyword: keyword, status: s, payoutProvider: provider, startTime: start, endTime: end, order: sortOrder,
      });
      if (sequence !== requestSequence.current) return;
      setItems(result.items);
      setTotal(result.total);
      setPendingTotalCount(result.pending_total_count ?? 0);
      setPendingTotalAmountFen(result.pending_total_amount_fen ?? 0);
      setPendingTotalAmountYuan(result.pending_total_amount_yuan ?? '0.00');
    } catch (e) {
      if (sequence === requestSequence.current) setError(resolveError(e));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [marketId, pageSize]);

  useEffect(() => {
    load(page, userId, userKeyword, status, payoutProvider, startTime, endTime, order);
    return () => { requestSequence.current += 1; };
  }, [endTime, load, order, page, payoutProvider, startTime, userId, userKeyword, status]);

  const setMarketId = useCallback((value: string) => {
    updateParams({ marketId: value, payoutProvider: null, page: null });
  }, [updateParams]);

  const setStatus = useCallback((s: string) => {
    updateParams({ status: s || null, page: null });
  }, [updateParams]);

  const setPayoutProvider = useCallback((value: string) => {
    updateParams({ payoutProvider: value || null, page: null });
  }, [updateParams]);

  const setUserId = useCallback((value: string) => {
    updateParams({ userId: value || null, page: null });
  }, [updateParams]);

  const setUserKeyword = useCallback((value: string) => {
    updateParams({ userKeyword: value.trim() || null, page: null });
  }, [updateParams]);

  const setStartTime = useCallback((value: string) => {
    updateParams({ startTime: value || null, page: null });
  }, [updateParams]);

  const setEndTime = useCallback((value: string) => {
    updateParams({ endTime: value || null, page: null });
  }, [updateParams]);

  const setOrder = useCallback((value: WithdrawalSortOrder) => {
    updateParams({ order: value === 'desc' ? null : value, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({ marketId }, { replace: true });
  }, [marketId, setSearchParams]);

  const reload = useCallback(() => {
    load(page, userId, userKeyword, status, payoutProvider, startTime, endTime, order);
  }, [endTime, load, order, page, payoutProvider, startTime, userId, userKeyword, status]);

  const review = useCallback(async (
    withdrawalId: number,
    action: 'approve' | 'reject',
    reviewNote: string,
    options?: {
      forceManual?: boolean;
    },
  ) => {
    setSubmitting(true);
    try {
      const result = await withdrawalsService.reviewWithdrawal(withdrawalId, action, reviewNote, options);
      if (action === 'approve') {
        let successMessage = '审核通过';
        switch (result.next_action) {
          case 'manual_settlement':
            successMessage = options?.forceManual
              ? '审核通过，已按操作要求转入人工处理'
              : '审核通过，当前将进入人工处理';
            break;
          case 'await_auto_payout_create':
            successMessage = '审核通过，已进入自动打款队列';
            break;
          case 'await_auto_payout_result':
            successMessage = '审核通过，自动打款处理中';
            break;
          default:
            successMessage = '审核通过';
            break;
        }
        addToast('success', successMessage);
      } else {
        addToast('success', '已拒绝');
      }
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const reviewMany = useCallback(async (
    withdrawalIds: number[],
    action: 'approve' | 'reject',
    reviewNote: string,
    options?: {
      forceManual?: boolean;
    },
  ) => {
    if (withdrawalIds.length === 0) return;
    setSubmitting(true);
    try {
      await withdrawalsService.reviewWithdrawals(withdrawalIds, action, reviewNote, options);
      addToast('success', `${action === 'approve' ? '批量通过' : '批量拒绝'} ${withdrawalIds.length} 条提现`);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
      reload();
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const updateTarget = useCallback(async (
    withdrawalId: number,
    payload: {
      channel: 'bank_card' | 'alipay';
      accountName: string;
      bankName?: string;
      bankCardNumber?: string;
      alipayAccount?: string;
    },
  ): Promise<boolean> => {
    setSubmitting(true);
    try {
      await withdrawalsService.updateWithdrawalTarget({ withdrawalId, ...payload });
      addToast('success', '收款信息已更新');
      reload();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const settle = useCallback(async (
    withdrawalId: number,
    payload?: {
      settlementNote?: string;
      paymentProofPath?: string;
    },
  ) => {
    setSubmitting(true);
    try {
      await withdrawalsService.settleWithdrawalWithPayload(withdrawalId, payload ?? {});
      addToast('success', '已标记结算');
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const settleMany = useCallback(async (
    withdrawalIds: number[],
    payload?: {
      settlementNote?: string;
    },
  ) => {
    if (withdrawalIds.length === 0) return;
    setSubmitting(true);
    try {
      for (const withdrawalId of withdrawalIds) {
        await withdrawalsService.settleWithdrawalWithPayload(withdrawalId, {
          settlementNote: payload?.settlementNote ?? '',
        });
      }
      addToast('success', `批量标记到账 ${withdrawalIds.length} 条提现`);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
      reload();
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const loadDetail = useCallback(async (withdrawalId: number): Promise<WithdrawalDetailDTO> => {
    try {
      return await withdrawalsService.getWithdrawalDetail(withdrawalId);
    } catch (e) {
      throw new Error(resolveError(e));
    }
  }, []);

  const queryPayout = useCallback(async (withdrawalId: number): Promise<boolean> => {
    setSubmitting(true);
    try {
      await withdrawalsService.queryPayout(withdrawalId);
      addToast('success', '已查询三方状态并写入详情记录');
      reload();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const startPayout = useCallback(async (withdrawalId: number): Promise<boolean> => {
    setSubmitting(true);
    try {
      await withdrawalsService.startPayout(withdrawalId);
      addToast('success', '已排入自动打款队列');
      reload();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const retryPayout = useCallback(async (withdrawalId: number): Promise<boolean> => {
    setSubmitting(true);
    try {
      await withdrawalsService.retryPayout(withdrawalId);
      addToast('success', '已重新排入自动打款队列');
      reload();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const switchToManual = useCallback(async (withdrawalId: number): Promise<boolean> => {
    setSubmitting(true);
    try {
      await withdrawalsService.switchToManual(withdrawalId);
      addToast('success', '已切换为人工处理');
      reload();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const loadReconcileReport = useCallback(async (
    staleMinutes: number,
    limit: number,
  ): Promise<PayoutReconcileReportDTO> => {
    try {
      return await withdrawalsService.getPayoutReconcileReport({ staleMinutes, limit });
    } catch (e) {
      throw new Error(resolveError(e));
    }
  }, []);

  const runReconcile = useCallback(async (
    staleMinutes: number,
    limit: number,
  ): Promise<PayoutReconcileRunResultDTO | null> => {
    setSubmitting(true);
    try {
      const result = await withdrawalsService.runPayoutReconcile({ staleMinutes, limit });
      addToast(
        'success',
        `已补偿入队 ${result.queued_create_jobs + result.queued_processing_jobs + result.queued_failed_jobs} 条任务`,
      );
      reload();
      return result;
    } catch (e) {
      addToast('error', resolveError(e));
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    marketId, setMarketId, items, total, pendingTotalCount, pendingTotalAmountFen, pendingTotalAmountYuan, page, pageSize, totalPages,
    userId, userKeyword, startTime, endTime, status, payoutProvider, order, loading, error, submitting,
    setPage, setPageSize, setUserId, setUserKeyword, setStartTime, setEndTime, setStatus, setPayoutProvider, setOrder, resetFilters, reload, review, reviewMany, updateTarget, settle, settleMany,
    loadDetail, queryPayout, startPayout, retryPayout, switchToManual,
    loadReconcileReport, runReconcile,
  };
}
